import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Chip, Divider, Skeleton, Typography } from "@mui/material";

import { getEntityProfileEditRoute } from "../../../../../../routers/routeUtils";
import { CountryWithBranches } from "../../../../../../features/Entity/api/types/entityLocationsByCountry.types";
import { useGetEntityLocationsByCountry } from "../../../../../../features/Entity/api/queries/useGetEntityLocationsByCountry";

import EmptyAchievementsFigure from "../../../../assets/images/empty-entity-achievements.png";

import { BranchItem, BranchItemSkeleton } from "./BranchItem";

export const EntityLocations = ({ canModify = false }: { canModify?: boolean }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityLocations" });
  const [currentLocation, setCurrentLocation] = useState<CountryWithBranches | null>(null);

  const { entityId } = useParams();
  const { data: locations, isLoading: isLoadingLocations } = useGetEntityLocationsByCountry({ entityId: entityId! });

  useEffect(() => {
    if (!locations || locations?.length === 0) return;

    if (currentLocation) {
      const updatedLocation = locations.find((location) => location.country.id === currentLocation.country.id);
      if (updatedLocation) {
        setCurrentLocation(updatedLocation);
        return;
      }
    }

    setCurrentLocation(locations[0]);
  }, [locations, currentLocation?.country.id]);

  if (!canModify && !isLoadingLocations && locations?.length === 0) return <NoLocations />;

  const handleCountryClick = (location: CountryWithBranches) => setCurrentLocation(location);

  return (
    <>
      {!canModify && <Typography sx={styles.title}>{t("locations")}</Typography>}
      <Box sx={styles.locationsContainer}>
        <Box sx={styles.countriesContainer}>
          <Box sx={styles.countriesWrapper}>
            {isLoadingLocations &&
              Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton key={idx} variant="rounded" width={80} height={20} />
              ))}
            {!isLoadingLocations &&
              locations &&
              locations?.map((location) => (
                <Chip
                  key={location.country.id}
                  label={location.country.name}
                  sx={styles.countryChip(location.country.id === currentLocation?.country.id)}
                  onClick={() => handleCountryClick(location)}
                />
              ))}
          </Box>
        </Box>
        <Box sx={styles.branchesContainer}>
          {isLoadingLocations &&
            Array.from({ length: 3 }).map((_, idx) => (
              <Box key={idx}>
                <BranchItemSkeleton />
                {idx !== 2 && <Divider flexItem orientation="horizontal" />}
              </Box>
            ))}
          {!isLoadingLocations && !currentLocation?.branches.length && (
            <Typography sx={styles.noBranchesText}>{t("noBranches")}</Typography>
          )}
          {currentLocation?.branches.map(({ id, name, address, isHeadquarter, googleMapsLink }, idx) => (
            <Box key={id}>
              <BranchItem
                id={id}
                name={name}
                address={address}
                isHeadquarter={isHeadquarter}
                googleMapsLink={googleMapsLink}
                canModify={canModify}
              />
              {idx !== currentLocation?.branches.length - 1 && <Divider flexItem orientation="horizontal" />}
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export const NoLocations = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityLocations" });

  const { entityId } = useParams();
  const navigate = useNavigate();
  const handleAddLocation = () => navigate(getEntityProfileEditRoute(entityId!, "locations"));

  return (
    <>
      <Typography
        sx={{
          color: "var(--black-100, #1C1C1C)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "24px",
        }}
      >
        {t("locations")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={EmptyAchievementsFigure}
          alt="empty-achievements"
          style={{
            width: "73.51px",
            height: "73.483px",
          }}
        />
        <Typography
          sx={{
            color: "var(--Dark---1000, #111)",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
          }}
        >
          {t("noLocations")}
        </Typography>
        <Button variant="primary-md" onClick={handleAddLocation}>
          {t("addLocation")}
        </Button>
      </Box>
    </>
  );
};

const styles = {
  container: {
    display: "flex",
    padding: "20px",
    flexDirection: "column",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    gap: "16px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
  },
  locationsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  countriesContainer: {
    width: "100%",
    overflowX: "auto",
    whiteSpace: "nowrap",
    padding: "0 8px 8px",
  },
  countriesWrapper: {
    display: "flex",
    gap: "10px",
    width: "fit-content",
  },
  countryChip: (isActive: boolean) => ({
    borderRadius: "24px",
    border: isActive ? "1px solid var(--Brand-600, #008B07)" : "none",
    background: isActive ? "var(--Brand-25, #F6FFF5)" : "transparent",
    color: isActive ? "var(--Green---100, #008B07)" : "var(--Dark---600, #707070)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  }),
  branchesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxHeight: "330px",
    overflowY: "auto",
    paddingRight: "8px",
  },
  noBranchesText: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};
